/**
 * Thin sport SVG assets used in the admin sport picker and icon picker.
 */
import React from 'react';
import { cn } from '@/lib/utils';

export const THIN_SPORT_ICON_OPTIONS = [
  { value: 'badminton', label: 'Badminton' },
  { value: 'baseball', label: 'Baseball' },
  { value: 'basketball', label: 'Basketball' },
  { value: 'biking', label: 'Biking' },
  { value: 'bowling', label: 'Bowling' },
  { value: 'chess', label: 'Chess' },
  { value: 'boxing', label: 'Boxing' },
  { value: 'cricket', label: 'Cricket' },
  { value: 'field-hockey', label: 'Field Hockey' },
  { value: 'football', label: 'Football' },
  { value: 'golf', label: 'Golf' },
  { value: 'paddle-tennis-racket', label: 'Paddle Tennis Racket' },
  { value: 'pickleball', label: 'Pickleball' },
  { value: 'ping-pong', label: 'Ping Pong' },
  { value: 'pool', label: 'Pool' },
  { value: 'rugby', label: 'Rugby' },
  { value: 'shuttlecock', label: 'Shuttlecock' },
  { value: 'snooker', label: 'Snooker' },
  { value: 'swimming', label: 'Swimming' },
  { value: 'tennis', label: 'Tennis' },
  { value: 'user-fast-running', label: 'Fitness' },
  { value: 'volleyball', label: 'Volleyball' },
  { value: 'yoga', label: 'Yoga' },
] as const;

export type ThinSportIconKey = (typeof THIN_SPORT_ICON_OPTIONS)[number]['value'];

const THIN_SPORT_ICON_META: Record<ThinSportIconKey, { label: string; src: string }> = {
  badminton: {
    label: 'Badminton',
    src: new URL('../../../assets/flaticon/sports/thin/badminton.svg', import.meta.url).href,
  },
  baseball: {
    label: 'Baseball',
    src: new URL('../../../assets/flaticon/sports/thin/baseball.svg', import.meta.url).href,
  },
  basketball: {
    label: 'Basketball',
    src: new URL('../../../assets/flaticon/sports/thin/basketball.svg', import.meta.url).href,
  },
  biking: {
    label: 'Biking',
    src: new URL('../../../assets/flaticon/sports/thin/biking.svg', import.meta.url).href,
  },
  bowling: {
    label: 'Bowling',
    src: new URL('../../../assets/flaticon/sports/thin/bowling.svg', import.meta.url).href,
  },
  chess: {
    label: 'Chess',
    src: new URL('../../../assets/flaticon/sports/thin/chess.svg', import.meta.url).href,
  },
  boxing: {
    label: 'Boxing',
    src: new URL('../../../assets/flaticon/sports/thin/boxing.svg', import.meta.url).href,
  },
  cricket: {
    label: 'Cricket',
    src: new URL('../../../assets/flaticon/sports/thin/cricket.svg', import.meta.url).href,
  },
  'field-hockey': {
    label: 'Field Hockey',
    src: new URL('../../../assets/flaticon/sports/thin/field hockey.svg', import.meta.url).href,
  },
  football: {
    label: 'Football',
    src: new URL('../../../assets/flaticon/sports/thin/football.svg', import.meta.url).href,
  },
  golf: {
    label: 'Golf',
    src: new URL('../../../assets/flaticon/sports/thin/golf.svg', import.meta.url).href,
  },
  'paddle-tennis-racket': {
    label: 'Paddle Tennis Racket',
    src: new URL('../../../assets/flaticon/sports/thin/paddle-tennis-racket.svg', import.meta.url).href,
  },
  pickleball: {
    label: 'Pickleball',
    src: new URL('../../../assets/flaticon/sports/thin/pickleball.svg', import.meta.url).href,
  },
  'ping-pong': {
    label: 'Ping Pong',
    src: new URL('../../../assets/flaticon/sports/thin/ping-pong.svg', import.meta.url).href,
  },
  pool: {
    label: 'Pool',
    src: new URL('../../../assets/flaticon/sports/thin/pool.svg', import.meta.url).href,
  },
  rugby: {
    label: 'Rugby',
    src: new URL('../../../assets/flaticon/sports/thin/rugby.svg', import.meta.url).href,
  },
  shuttlecock: {
    label: 'Shuttlecock',
    src: new URL('../../../assets/flaticon/sports/thin/shuttlecock.svg', import.meta.url).href,
  },
  snooker: {
    label: 'Snooker',
    src: new URL('../../../assets/flaticon/sports/thin/snooker.svg', import.meta.url).href,
  },
  swimming: {
    label: 'Swimming',
    src: new URL('../../../assets/flaticon/sports/thin/swimming.svg', import.meta.url).href,
  },
  tennis: {
    label: 'Tennis',
    src: new URL('../../../assets/flaticon/sports/thin/tennis.svg', import.meta.url).href,
  },
  'user-fast-running': {
    label: 'Fitness',
    src: new URL('../../../assets/flaticon/sports/thin/user-fast-running.svg', import.meta.url).href,
  },
  volleyball: {
    label: 'Volleyball',
    src: new URL('../../../assets/flaticon/sports/thin/Volleyball.svg', import.meta.url).href,
  },
  yoga: {
    label: 'Yoga',
    src: new URL('../../../assets/flaticon/sports/thin/yoga.svg', import.meta.url).href,
  },
};

export function normalizeThinSportIconKey(iconName: string | null | undefined): ThinSportIconKey | null {
  if (!iconName) return null;

  const normalized = iconName.trim().toLowerCase();
  if (!normalized) return null;

  if (normalized in THIN_SPORT_ICON_META) {
    return normalized as ThinSportIconKey;
  }

  return null;
}

export function getThinSportIconMeta(iconName: string | null | undefined): { label: string; src: string } | null {
  const normalized = normalizeThinSportIconKey(iconName);
  if (!normalized) return null;

  return THIN_SPORT_ICON_META[normalized];
}

export function ThinSportIcon({
  iconName,
  className,
  size = 16,
}: {
  iconName: string | null | undefined;
  className?: string;
  size?: number;
}) {
  const meta = getThinSportIconMeta(iconName);

  if (!meta) {
    return null;
  }

  return (
    <span
      aria-hidden="true"
      className={cn('shrink-0 inline-block bg-current', className)}
      style={{
        width: size,
        height: size,
        maskImage: `url(${meta.src})`,
        WebkitMaskImage: `url(${meta.src})`,
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
      }}
    />
  );
}
